import {
  type ApplicationValidationError,
  isLibraryInOut,
  isTemplateCreateOperation,
  isTemplateHideOperation,
  type TemplateApplicationValidationError,
  type TemplateDependencyValidationError,
} from '@festina/configuration-general-types/configuration'
import { dateUtils } from '@festina-life/core'
import { useMemo } from 'react'

import type { ValidationErrorWithLocation } from '../../uiModels/errors/types'
import { useConfigurationOperationErrorsMap } from '../../uiModels/errors/useConfigurationOperationErrorsMap'
import {
  getTemplateOperationFromDate,
  getTemplateOperationName,
} from '../../uiModels/templateOperations/helpers'
import type { TemplateOperation } from '../../uiModels/templateOperations/types'
import type { OperationWithSourceInfo } from '../../uiModels/types'
import { useOperationsContext } from '../../workspace/operationsContext'
import { LocationAwareOperationLink } from '../configurationOperationLink/LocationAwareOperationLink'
import {
  listIdsWithOperationsOrErrors,
  resolveOperationLocation,
} from './helpers'
import type { OperationsListVariant } from './types'

type Props = {
  operations: OperationWithSourceInfo<TemplateOperation>[]
  errors: ValidationErrorWithLocation<
    | TemplateApplicationValidationError
    | TemplateDependencyValidationError
    | ApplicationValidationError
  >[]
  variant: OperationsListVariant
}

export const TemplateOperationsWithErrors = ({
  errors,
  operations,
  variant,
}: Props) => {
  const { workspaceKey, branchKey } = useOperationsContext()

  const templateErrors = useConfigurationOperationErrorsMap({
    operations,
    errors,
    idFromOperation: (operation) =>
      getTemplateOperationName(operation.internalOperation),
    idFromError: (error) => error.operationName ?? '',
  })

  const operationsMap = useMemo(
    () =>
      operations.reduce((acc, operation) => {
        const operationName = getTemplateOperationName(
          operation.internalOperation,
        )
        if (!acc.get(operationName)) {
          acc.set(operationName, [])
        }
        acc.get(operationName)!.push(operation)
        return acc
      }, new Map<string, OperationWithSourceInfo<TemplateOperation>[]>()),
    [operations],
  )

  const allTemplateIds = listIdsWithOperationsOrErrors(
    Array.from(operationsMap.keys()),
    templateErrors,
  )

  return allTemplateIds.map((templateId) => {
    const templateIdErrors = templateErrors?.get(templateId)
    const templateIdOperations = operationsMap.get(templateId)
    const operationKeySet = listIdsWithOperationsOrErrors(
      templateIdOperations?.map(
        ({ internalOperation }) => internalOperation.operationKey,
      ) ?? [],
      templateIdErrors,
    )

    return operationKeySet.map((operationKey) => {
      const templateOperation = templateIdOperations?.find(
        ({ internalOperation }) =>
          internalOperation.operationKey === operationKey,
      )
      const templateErrors = templateIdErrors?.get(operationKey)
      const errorCount = templateErrors?.length ?? 0

      const fromDateString = templateOperation
        ? getTemplateOperationFromDate(templateOperation.internalOperation)
        : templateErrors?.[0]?.error.operationFromDate

      const fromDate = fromDateString
        ? dateUtils.dateUtcAsLocal(fromDateString).toDate()
        : undefined
      const isHideOperation = templateOperation
        ? isTemplateHideOperation(templateOperation.internalOperation)
        : templateErrors?.[0]?.error.operationClass === 'TemplateHideOperation'

      const isLibrary = templateOperation
        ? !isHideOperation &&
          isTemplateCreateOperation(templateOperation.internalOperation) &&
          isLibraryInOut(templateOperation.internalOperation.entity)
        : templateErrors?.[0]?.error.operationName?.endsWith('Library') // If the operation isn't loading we can only guess the type from the operation name

      let templateType: 'hideTemplate' | 'library' | 'template'
      if (isHideOperation) {
        templateType = 'hideTemplate'
      } else if (isLibrary) {
        templateType = 'library'
      } else {
        templateType = 'template'
      }

      const shouldHideOperationLink =
        variant === 'errors' ? !errorCount : !templateOperation

      // If there is no templateOperation the only way the operationKey can be processed is if there's at least one error with that key.
      const { layer, block } = resolveOperationLocation(
        templateOperation,
        templateErrors,
      )

      return shouldHideOperationLink ? null : (
        <LocationAwareOperationLink
          key={operationKey}
          layer={layer}
          block={block}
          itemId={templateId}
          type={templateType}
          workspaceKey={templateOperation ? workspaceKey : undefined} // If there's no operation then the operation belongs to the branch and not the workspace
          branchKey={workspaceKey?.branchKey ?? branchKey}
          errorCount={errorCount}
          fromDate={fromDate}
          operation={templateOperation?.internalOperation}
          operationKey={templateOperation?.internalOperation.operationKey}
          isDeleteEnabled={variant === 'operations'}
          isDraft={!!templateOperation?.isDraft}
        />
      )
    })
  })
}
